type PreviewPlayer = {
  currentTime(): Promise<number>;
  seek(seconds: number): Promise<unknown>;
  play(): Promise<unknown>;
  pause(): Promise<unknown>;
};

// Serialize playback commands: an older pending play must not undo a newer stop.
export function createTrimPreview(
  player: PreviewPlayer,
  onError: () => void,
  onTime?: (seconds: number) => void,
) {
  let revision = 0;
  let disposed = false;
  let end: number | null = null;
  let timer: ReturnType<typeof setInterval> | undefined;
  let checking = false;
  let reportedError = false;
  let commands = Promise.resolve();
  let cancelPending: (() => void) | undefined;
  const perform = async (command: () => Promise<unknown>) => {
    let cancel!: () => void;
    let cancellationTimer: ReturnType<typeof setTimeout> | undefined;
    const cancelled = new Promise<void>((resolve) => {
      cancel = () => {
        // Let an already-issued SDK command settle before issuing the next.
        // Vimeo replies are matched by method, so overlapping pause/seek/play
        // calls can otherwise acknowledge the wrong request.
        cancellationTimer ??= setTimeout(resolve, 300);
      };
    });
    cancelPending = cancel;
    try {
      // Seeking or starting playback may remain pending during buffering.
      // A newer action supersedes it; revision checks discard its continuation.
      await Promise.race([command(), cancelled]);
    } finally {
      clearTimeout(cancellationTimer);
      if (cancelPending === cancel) cancelPending = undefined;
    }
  };
  const enqueue = (command: () => Promise<void>) => {
    const result = commands.then(command);
    commands = result.catch(() => {});
    return result;
  };
  const clear = () => {
    end = null;
    clearInterval(timer);
    timer = undefined;
  };
  const report = () => {
    if (!disposed && !reportedError) {
      reportedError = true;
      onError();
    }
  };
  const check = async () => {
    if (disposed || checking || (end === null && !onTime)) return;
    checking = true;
    const current = revision;
    const boundary = end;
    try {
      // Read the current playhead rather than trusting delayed seek/time events.
      const seconds = await player.currentTime();
      if (current !== revision) return;
      onTime?.(seconds);
      if (boundary === null || seconds < boundary) return;
      await enqueue(async () => {
        if (current !== revision) return;
        await perform(() => player.pause());
        if (current !== revision) return;
        clear();
        // Leave the playhead at the selected end, even after a delayed tick.
        await perform(() => player.seek(boundary));
      });
    } catch {
      if (current === revision) report();
      // Keep monitoring when pause fails, so a transient error cannot remove
      // the boundary and leave the rest of the video playing.
    } finally {
      checking = false;
    }
  };
  // Keep the editor in sync during ordinary playback and paused seeks too.
  // Vimeo's timeupdate events alone do not reliably cover all of those cases.
  const syncTimer = onTime ? setInterval(() => void check(), 100) : undefined;
  return {
    check: () => void check(),
    preview(start: number, finish: number) {
      if (
        !Number.isFinite(start) ||
        !Number.isFinite(finish) ||
        start < 0 ||
        finish <= start
      )
        return Promise.reject(new Error("Select a valid preview range."));
      if (disposed) return Promise.reject(new Error("The video has closed."));
      cancelPending?.();
      const current = ++revision;
      clear();
      reportedError = false;
      return enqueue(async () => {
        if (current !== revision) return;
        await perform(() => player.pause());
        if (current !== revision) return;
        await perform(() => player.seek(start));
        if (current !== revision) return;
        await perform(() => player.play());
        if (current !== revision) return;
        end = finish;
        timer = setInterval(() => void check(), 100);
        void check();
      });
    },
    stop() {
      cancelPending?.();
      const current = ++revision;
      clear();
      return enqueue(async () => {
        if (current !== revision) return;
        await perform(() => player.pause());
      }).catch(report);
    },
    seek(seconds: number, play = false) {
      cancelPending?.();
      const current = ++revision;
      clear();
      return enqueue(async () => {
        if (disposed || current !== revision) return;
        await perform(() => player.pause());
        if (current !== revision) return;
        await perform(() => player.seek(seconds));
        if (current !== revision) return;
        // Handle selection plays from the boundary; timeline scrubbing pauses.
        if (play) await perform(() => player.play());
        else await perform(() => player.pause());
        if (current === revision) void check();
      });
    },
    dispose() {
      disposed = true;
      clearInterval(syncTimer);
      void this.stop();
    },
  };
}
